import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import { useStatsigClient } from '@statsig/react-bindings';
import { Alert, Button, CircularProgress, Stack } from '@mui/material';
import { QuickCreateCommunicationProvider } from 'components/drawers/quick-create-communication-drawer/quick-create-communication.context';
import { useGetGuestJourneySettingsQuery } from 'api/client-domain/client-domain';
import IncPageBody from 'components/inc-page/inc-page-body/inc-page-body.component';
import IncPageHeader from 'components/inc-page/inc-page-header/inc-page-header.component';
import IncPageTabPanels from 'components/inc-page/inc-page-tab-panels/inc-page-tab-panels.component';
import { IncPageTabPanelsProvider } from 'components/inc-page/inc-page-tab-panels/inc-page-tab-panels.context';
import { useSetInitialTabAsDefault } from 'components/inc-page/inc-page-tab-panels/inc-page-tab-panels.hooks';
import IncPageTitle from 'components/inc-page/inc-page-title/inc-page-title.component';
import IncPage from 'components/inc-page/inc-page.component';
import { useHasAllMerchantLocationAccess } from 'hooks/authorization.hooks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { GUEST_JOURNEY_TAB_GATES } from './guest-journey-tab-gates';
import { useGuestJourneyStatus } from './guest-journey-status.hooks';
import GuestJourneyPendingState from './report-states/guest-journey-pending-state.component';
import GuestJourneySettingsProcessingBanner from './report-states/guest-journey-settings-processing-banner.component';

const GuestJourney = () => {
  const { t } = useTranslation();
  const { client } = useStatsigClient();

  // Benchmarks is brand-level data (no location filter), so it's restricted to
  // all-location admins per the PRD. Location-scoped admins don't see the tab
  // (and the route element redirects them if they reach it by URL — sc-374570).
  const hasAllLocationAccess = useHasAllMerchantLocationAccess();

  // Settings define the stage taxonomy every tab renders from — gate the tab
  // panels until they load. RTK Query caches the result, so individual views
  // re-reading it via useGuestJourneyStageConfig hit the same cache entry.
  const {
    data: settings,
    isFetching: settingsFetching,
    isError: settingsError,
    refetch: refetchSettings,
  } = useGetGuestJourneySettingsQuery();

  // Report-level readiness + settings-processing status, read once for the whole
  // report — drives the cross-tab pending state and settings-processing banner.
  // Purely additive: it never gates or blocks the report, so an absent/failed
  // status just renders the normal report rather than erroring it out.
  const status = useGuestJourneyStatus();

  // Each tab is shown only when its Statsig gate is on. Filtering here hides the
  // tab button; the matching per-route gate in main-routes prunes the route so a
  // hidden tab can't be reached by URL either.
  const tabs = useMemo(
    () =>
      [
        {
          label: t('page.guestJourney.tabs.activeCustomers.label'),
          tooltip: t('page.guestJourney.tabs.activeCustomers.tooltip'),
          path: 'active-customers',
          gate: GUEST_JOURNEY_TAB_GATES['active-customers'],
        },
        {
          label: t('page.guestJourney.tabs.sliders.label'),
          tooltip: t('page.guestJourney.tabs.sliders.tooltip'),
          path: 'sliders',
          gate: GUEST_JOURNEY_TAB_GATES.sliders,
        },
        {
          label: t('page.guestJourney.tabs.churned.label'),
          tooltip: t('page.guestJourney.tabs.churned.tooltip'),
          path: 'churned',
          gate: GUEST_JOURNEY_TAB_GATES.churned,
        },
        {
          label: t('page.guestJourney.tabs.offerImpact.label'),
          tooltip: t('page.guestJourney.tabs.offerImpact.tooltip'),
          path: 'offer-impact',
          gate: GUEST_JOURNEY_TAB_GATES['offer-impact'],
        },
        {
          label: t('page.guestJourney.tabs.retention.label'),
          tooltip: t('page.guestJourney.tabs.retention.tooltip'),
          path: 'retention',
          gate: GUEST_JOURNEY_TAB_GATES.retention,
        },
        {
          label: t('page.guestJourney.tabs.benchmarks.label'),
          tooltip: t('page.guestJourney.tabs.benchmarks.tooltip'),
          path: 'benchmarks',
          gate: GUEST_JOURNEY_TAB_GATES.benchmarks,
        },
        {
          label: t('page.guestJourney.tabs.stageMovement.label'),
          tooltip: t('page.guestJourney.tabs.stageMovement.tooltip'),
          path: 'stage-movement',
          gate: GUEST_JOURNEY_TAB_GATES['stage-movement'],
        },
        {
          label: t('page.guestJourney.tabs.details.label'),
          tooltip: t('page.guestJourney.tabs.details.tooltip'),
          path: 'details',
          gate: GUEST_JOURNEY_TAB_GATES.details,
        },
      ].filter(
        tab =>
          client.checkGate(tab.gate) &&
          (tab.path !== 'benchmarks' || hasAllLocationAccess),
      ),
    [t, client, hasAllLocationAccess],
  );

  useSetInitialTabAsDefault(tabs);

  return (
    <IncPage>
      <IncPageHeader>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <IncPageTitle />
          <Button
            variant="text"
            component={RouterLink}
            to="settings"
            startIcon={<SettingsOutlined />}
            data-testid="GuestJourney.SettingsLink"
          >
            {t('page.guestJourney.settings.link')}
          </Button>
        </Stack>
      </IncPageHeader>
      <IncPageBody>
        {settings ? (
          status?.ready === false ? (
            <GuestJourneyPendingState
              requiredTransactions={status.requiredTransactions}
            />
          ) : (
            <QuickCreateCommunicationProvider>
              <Stack spacing={2}>
                {status?.settingsProcessing && (
                  <GuestJourneySettingsProcessingBanner />
                )}
                <IncPageTabPanelsProvider
                  tabs={tabs}
                  options={{ keepSearchParams: true }}
                >
                  <IncPageTabPanels />
                </IncPageTabPanelsProvider>
              </Stack>
            </QuickCreateCommunicationProvider>
          )
        ) : settingsFetching ? (
          <Stack
            alignItems="center"
            paddingY={8}
            data-testid="GuestJourney.SettingsLoading"
          >
            <CircularProgress />
          </Stack>
        ) : settingsError ? (
          <Alert
            severity="error"
            data-testid="GuestJourney.SettingsError"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => refetchSettings()}
                data-testid="GuestJourney.SettingsError.Retry"
              >
                {t('page.guestJourney.settingsError.retry')}
              </Button>
            }
          >
            {t('page.guestJourney.settingsError.message')}
          </Alert>
        ) : null}
      </IncPageBody>
    </IncPage>
  );
};

export default GuestJourney;
